import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

type ButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

interface ButtonBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export type ButtonProps =
  | (ButtonBaseProps & { size?: Exclude<ButtonSize, 'icon'> })
  | (ButtonBaseProps & { 'aria-label': string; 'aria-labelledby'?: string; size: 'icon' })
  | (ButtonBaseProps & { 'aria-label'?: string; 'aria-labelledby': string; size: 'icon' });

const variants: Record<ButtonVariant, string> = {
  default: 'bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)]',
  secondary: 'bg-[var(--secondary-light)] text-[var(--foreground)] hover:opacity-90',
  outline: 'border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--background-muted)]',
  ghost: 'text-[var(--foreground)] hover:bg-[var(--background-muted)]',
  destructive: 'bg-[var(--destructive-light)] text-[var(--foreground)] hover:opacity-90',
};

const sizes: Record<ButtonSize, string> = {
  default: 'h-9 px-4 py-2',
  sm: 'h-8 rounded-[var(--radius-control)] px-3 text-xs',
  lg: 'h-10 px-6',
  icon: 'size-9 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'default', type = 'button', ...props },
  ref,
) {
  if (size === 'icon' && props['aria-label'] === undefined && props['aria-labelledby'] === undefined) {
    throw new Error('Icon buttons require an accessible name.');
  }

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]',
        variants[variant],
        sizes[size],
        className,
      )}
      ref={ref}
      type={type}
      {...props}
    />
  );
});

Button.displayName = 'Button';
